import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { logger } from './logger';

interface OfflineRun {
  id: string;
  userId: string;
  startedAt: number;
  finishedAt: number;
  duration: number;
  distance: number;
  pace: number;
  calories: number;
  emotion?: string;
  caption?: string;
  splits: Array<{ km: number; time: number; pace: number; latitude?: number; longitude?: number }>;
  gpsPoints: Array<{ lat: number; lng: number; timestamp: number; speed: number; accuracy: number }>;
  bpmReadings?: Array<{ timestamp: number; bpm: number }>;
  postureData?: any;
  musicStats?: any;
  syncStatus: 'pending' | 'syncing' | 'failed' | 'synced';
  syncAttempts: number;
  lastSyncAttempt?: number;
  failureReason?: string;
}

interface OfflineDB extends DBSchema {
  'pending-runs': {
    key: string;
    value: OfflineRun;
    indexes: { 
      'by-user': string; 
      'by-status': string;
      'by-date': number;
    };
  };
  'sync-metadata': {
    value: {
      key: string;
      lastSyncAt: number;
      totalPendingRuns: number;
      totalSyncedRuns: number;
      lastError?: string;
    };
    key: string;
  };
}

class OfflineDatabase {
  private db: IDBPDatabase<OfflineDB> | null = null;

  async init() {
    try {
      this.db = await openDB<OfflineDB>('ybr-offline', 2, {
        upgrade(db) {
          if (!db.objectStoreNames.contains('pending-runs')) {
            const store = db.createObjectStore('pending-runs', { keyPath: 'id' });
            store.createIndex('by-user', 'userId');
            store.createIndex('by-status', 'syncStatus');
            store.createIndex('by-date', 'finishedAt');
          }

          if (!db.objectStoreNames.contains('sync-metadata')) {
            db.createObjectStore('sync-metadata', { keyPath: 'key' });
          }
        }
      });

      logger.info('Offline database initialized', {
        version: this.db.version
      });
    } catch (error) {
      logger.error('Failed to initialize offline DB', error);
      throw error;
    }
  }

  async saveOfflineRun(run: OfflineRun): Promise<void> {
    if (!this.db) await this.init();
    
    try {
      await this.db!.put('pending-runs', run);
      await this.updateSyncMetadata();
      
      logger.info('Run saved offline', { 
        runId: run.id, 
        distance: run.distance 
      });
    } catch (error) {
      logger.error('Failed to save offline run', error, { runId: run.id });
      throw error;
    }
  }

  async getPendingRuns(userId: string): Promise<OfflineRun[]> {
    if (!this.db) await this.init();
    
    try {
      const runs = await this.db!.getAllFromIndex(
        'pending-runs', 
        'by-user', 
        userId
      );
      
      return runs.filter(r => r.syncStatus !== 'synced')
                .sort((a, b) => a.finishedAt - b.finishedAt);
    } catch (error) {
      logger.error('Failed to get pending runs', error);
      return [];
    }
  }

  async updateRunSyncStatus(
    runId: string, 
    status: OfflineRun['syncStatus'],
    failureReason?: string
  ): Promise<void> {
    if (!this.db) await this.init();
    
    try {
      const run = await this.db!.get('pending-runs', runId);
      if (!run) {
        throw new Error(`Run ${runId} not found`);
      }

      run.syncStatus = status;
      run.syncAttempts++;
      run.lastSyncAttempt = Date.now();
      if (failureReason) run.failureReason = failureReason;

      await this.db!.put('pending-runs', run);
      await this.updateSyncMetadata();
      
      logger.info('Run sync status updated', { 
        runId, 
        status, 
        attempts: run.syncAttempts 
      });
    } catch (error) {
      logger.error('Failed to update sync status', error, { runId });
      throw error;
    }
  }

  async deleteRun(runId: string): Promise<void> {
    if (!this.db) await this.init();
    
    try {
      await this.db!.delete('pending-runs', runId);
      await this.updateSyncMetadata();
      
      logger.info('Offline run deleted', { runId });
    } catch (error) {
      logger.error('Failed to delete offline run', error, { runId });
      throw error;
    }
  }

  private async updateSyncMetadata(): Promise<void> {
    if (!this.db) return;
    
    try {
      const allRuns = await this.db.getAll('pending-runs');
      const pending = allRuns.filter(r => r.syncStatus === 'pending' || r.syncStatus === 'failed');
      const synced = allRuns.filter(r => r.syncStatus === 'synced');

      await this.db.put('sync-metadata', {
        key: 'main',
        lastSyncAt: Date.now(),
        totalPendingRuns: pending.length,
        totalSyncedRuns: synced.length
      });
    } catch (error) {
      logger.error('Failed to update sync metadata', error);
    }
  }

  async getSyncMetadata() {
    if (!this.db) await this.init();
    
    try {
      return await this.db!.get('sync-metadata', 'main');
    } catch (error) {
      logger.error('Failed to get sync metadata', error);
      return null;
    }
  }
}

export const offlineDB = new OfflineDatabase();
export type { OfflineRun };
